import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CardOrQuote } from "@/components/CardOrQuote";
import { LaunchRibbon } from "@/components/LaunchRibbon";
import { StockBadge } from "@/components/StockBadge";
import Seo from "@/components/Seo";
import { SectionHeading, Eyebrow } from "@/components/ui-bits";
import { PRODUCTS, INVERTER_PRODUCTS, PANEL_PRODUCTS } from "@/data/site";

const galleryImages = (p) => ((p.images && p.images.length ? p.images : [p.image]).filter(Boolean).slice(0, 4));

const Thumbs = ({ imgs, active, setActive, slug, size = "w-14 h-14" }) =>
  imgs.length > 1 ? (
    <div className="flex gap-2" data-testid={`thumbs-${slug}`}>
      {imgs.map((src, i) => (
        <button key={i} type="button" onMouseEnter={() => setActive(i)} onClick={() => setActive(i)}
          className={`${size} rounded-sm overflow-hidden border transition-colors bg-ei-navy ${active === i ? "border-ei-gold" : "border-white/20 hover:border-white/50"}`}
          data-testid={`thumb-${slug}-${i}`} aria-label={`View image ${i + 1}`}>
          <img src={src} alt="" className="w-full h-full object-contain p-1" />
        </button>
      ))}
    </div>
  ) : null;

const ProductCard = ({ p }) => {
  const imgs = galleryImages(p);
  const [active, setActive] = useState(0);
  return (
    <div className="flex flex-col border border-ei-mist rounded-md bg-white overflow-hidden" data-testid={`product-card-${p.slug}`}>
      <div className="relative aspect-[16/10] bg-ei-navy border-b border-ei-mist overflow-hidden">
        {!p.component && p.originalPrice && <LaunchRibbon testId={`launch-ribbon-${p.slug}`} />}
        {imgs.length ? (
          <img src={imgs[Math.min(active, imgs.length - 1)]} alt={p.imageAlt || p.name} loading="lazy" className={`w-full h-full object-contain ${["cygni-all-in-one", "power-stack-pro-100", "power-box-g2"].includes(p.slug) && active === 0 ? "p-0" : "p-6 sm:p-8"}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-ei-graphite/50 px-6 text-center">Product photography being supplied</div>
        )}
      </div>
      {imgs.length > 1 && (
        <div className="bg-ei-navy border-b border-ei-mist px-4 py-3 flex justify-center">
          <Thumbs imgs={imgs} active={active} setActive={setActive} slug={p.slug} />
        </div>
      )}
      <div className="p-6 sm:p-8 flex-1 flex flex-col">
        <Eyebrow>{p.category}</Eyebrow>
        <h2 className="mt-3 text-xl font-semibold text-ei-navy flex flex-wrap items-center gap-2">{p.name} <StockBadge slug={p.slug} testId={`stock-${p.slug}`} />{p.nameNote && <span className="block w-full text-sm font-normal text-ei-graphite/60">({p.nameNote})</span>}</h2>
        <p className="mt-3 text-sm text-ei-graphite/85 leading-relaxed flex-1">{p.summary}</p>
        <div className="mt-6">
          <div className={`font-display font-bold tnum ${p.hasPublicPrice ? "text-ei-navy text-xl" : "text-ei-golddark text-base"}`}>
            <span data-testid={`product-price-${p.slug}`}>{p.priceLabel}<span className="ml-1 text-sm font-normal text-current opacity-80">+ GST</span></span>
          </div>
          {p.priceUnit && <div className="mt-1 text-xs text-ei-graphite/60" data-testid={`product-price-unit-${p.slug}`}>{p.priceUnit}</div>}
          <p className="mt-1 text-xs text-ei-graphite/60">{p.priceNote}</p>
        </div>
      </div>
      <div className="p-6 sm:p-8 pt-0 flex flex-col gap-3">
        <CardOrQuote slug={p.slug} quoteName={p.name} image={imgs[0]} cardTestId={`product-buy-${p.slug}`} quoteTestId={`product-quote-${p.slug}`} />
        <Link to={`/products/${p.slug}`} className="w-full text-center font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-3 rounded-sm hover:border-ei-navy transition-colors min-h-[44px] inline-flex items-center justify-center" data-testid={`product-details-${p.slug}`}>
          Details
        </Link>
      </div>
    </div>
  );
};

const AccessoryCard = ({ p }) => {
  const imgs = galleryImages(p);
  const [active, setActive] = useState(0);
  return (
    <div className="flex flex-col border border-ei-mist rounded-md bg-white overflow-hidden" data-testid={`accessory-${p.slug}`}>
      <Link to={`/products/${p.slug}`} className="relative block w-full aspect-[16/10] bg-ei-navy overflow-hidden flex items-center justify-center p-4 hover:opacity-95 transition-opacity">
        {!p.component && p.originalPrice && <LaunchRibbon testId={`launch-ribbon-${p.slug}`} />}
        <img src={imgs[Math.min(active, imgs.length - 1)]} alt={p.imageAlt || p.name} loading="lazy" className="w-full h-full object-contain" />
      </Link>
      {imgs.length > 1 && (
        <div className="bg-ei-navy border-b border-ei-mist px-4 py-3 flex justify-center">
          <Thumbs imgs={imgs} active={active} setActive={setActive} slug={p.slug} />
        </div>
      )}
      <div className="p-6 sm:p-8 flex-1 flex flex-col">
        <div className="ei-eyebrow text-ei-golddark">{p.category}</div>
        <h3 className="mt-2 text-lg font-semibold text-ei-navy flex flex-wrap items-center gap-2">{p.name} <StockBadge slug={p.slug} testId={`stock-${p.slug}`} /></h3>
        <p className="mt-3 text-sm text-ei-graphite/85 leading-relaxed flex-1">{p.summary}</p>
        <div className="mt-4 font-display font-bold text-ei-navy text-xl">
          {p.originalPrice && <span className="text-ei-graphite/45 text-sm line-through mr-2 font-normal">{p.originalPrice}</span>}
          <span data-testid={`accessory-price-${p.slug}`}>{p.priceLabel}<span className="ml-1 text-sm font-normal text-current opacity-80">+ GST</span></span>
          {p.priceNote && <span className="block mt-1 text-xs font-normal text-ei-graphite/60">{p.priceNote}</span>}
        </div>
        <CardOrQuote slug={p.slug} quoteName={p.name} image={imgs[0]} cardTestId={`accessory-buy-${p.slug}`} quoteTestId={`accessory-quote-${p.slug}`} className="mt-5" />
        <Link to={`/products/${p.slug}`} className="mt-3 w-full text-center font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy transition-colors min-h-[44px] inline-flex items-center justify-center" data-testid={`accessory-details-${p.slug}`}>
          Details
        </Link>
      </div>
    </div>
  );
};

export default function Products() {
  return (
    <>
      <Seo title="Products — EI Battery Solutions" description="Wholesale batteries and selected hybrid inverters for registered Australian electrical contractors. Request a trade quote." />
      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow className="text-ei-gold">Launch range</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">Products</h1>
          <p className="mt-5 text-lg text-ei-white/75 max-w-2xl leading-relaxed">
            Our initial wholesale range for registered electrical contractors. Prices shown are wholesale (ex GST) per unit.
            We supply equipment only, to licensed contractors.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[...PRODUCTS.filter((p) => !p.draft && !p.component), ...PRODUCTS.filter((p) => !p.draft && p.component)].map((p) => <ProductCard key={p.slug} p={p} />)}
        </div>

        <div className="mt-16" data-testid="products-inverters">
          <h2 className="text-2xl font-bold text-ei-navy">Hybrid inverters</h2>
          <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl leading-relaxed">Single- and three-phase hybrid inverters supplied to match your battery choice. Final inverter selection and pricing are confirmed on your quote.</p>
          {[
            { key: "single", label: "Single phase", note: "For standard domestic supply." },
            { key: "three", label: "Three phase", note: "For three-phase domestic and light commercial supply." },
          ].map((group) => {
            const items = INVERTER_PRODUCTS.filter((p) => (p.phase || "single") === group.key);
            if (!items.length) return null;
            return (
              <div key={group.key} className="mt-8" data-testid={`inverters-${group.key}-phase`}>
                <h3 className="ei-eyebrow text-ei-gold">{group.label} · {items.length} models</h3>
                <p className="mt-1 text-sm text-ei-graphite/70">{group.note}</p>
                <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {items.map((p) => <AccessoryCard key={p.slug} p={p} />)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16" data-testid="products-panels">
          <h2 className="text-2xl font-bold text-ei-navy">Solar panels</h2>
          <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl leading-relaxed">Tier-1 solar panels supplied with our battery kits. Volume pricing is confirmed on your quote.</p>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {PANEL_PRODUCTS.map((p) => <AccessoryCard key={p.slug} p={p} />)}
          </div>
        </div>

        <p className="mt-10 text-xs text-ei-graphite/60 max-w-3xl leading-relaxed border-l-2 border-ei-gold/60 pl-4">
          Prices shown are indicative wholesale (ex GST) per unit and exclude freight. EI Battery Solutions supplies
          equipment only to licensed contractors. Product documents and photography are supplied by the manufacturer,
          Dyness. Final pricing is confirmed at quotation.
        </p>
      </section>
    </>
  );
}
